import { Colors, Img, Lightning, Router, Storage, Utils } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../components/input/Button/Button";
import theme from "../../lib/theme";
import { isPostTvLinux, isPostTvAndroid, STORAGE_KEYS } from "../../lib/utils";
import { postTvWebSocket } from "../../lib/websocket";

// Module-level storage for pending callbacks
let pendingCallbacks: {
    onSuccess?: () => void;
    onCancel?: () => void;
    submitLabel?: string;
    subtitleKey?: string;
} | null = null;

export function setPinInputCallbacks(
    onSuccess?: () => void,
    onCancel?: () => void,
    submitLabel?: string,
    subtitleKey?: string
) {
    pendingCallbacks = { onSuccess, onCancel, submitLabel, subtitleKey };
}

export function clearPinInputCallbacks() {
    pendingCallbacks = null;
}

const BOX_SIZE = 56;
const BOX_SPACING = 12;
const CARD_W = 500;
const CARD_H = 400;

interface PinInputTemplateSpec extends Lightning.Component.TemplateSpec {
    Overlay: object;
    Card: object;
}

export class PinInput
    extends Lightning.Component<PinInputTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PinInputTemplateSpec>
{
    private _pin: string[] = [];
    private _focusedBoxIndex: number = 0;
    private _onSuccess?: () => void;
    private _onCancel?: () => void;
    private _isVerifying: boolean = false;
    private _mode: "verify" | "set" | "confirm" | "age" = "verify";
    private _onPinSet?: (pin: string) => void;
    private _firstPin?: string;
    private _digitCount: number = 4;
    private _focusArea: "boxes" | "submit" = "boxes";

    static override _template(): Lightning.Component.Template<PinInputTemplateSpec> {
        const pinRowWidth = BOX_SIZE * 4 + BOX_SPACING * 3;

        return {
            zIndex: 50,
            w: theme.layout.w,
            h: theme.layout.h,
            alpha: 0,
            collision: true,
            // Dark overlay
            Overlay: {
                w: theme.layout.w,
                h: theme.layout.h,
                rect: true,
                color: Colors(theme.colors.bg.overlay || "#000000").alpha(0.7).get()
            },
            // Card
            Card: {
                w: CARD_W,
                h: CARD_H,
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                rect: true,
                color: 0xff2068c8,
                shader: { type: Lightning.shaders.RoundedRectangle, radius: 16 },
                flex: {
                    direction: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingTop: 24,
                    paddingBottom: 24
                },
                Logo: {
                    flexItem: { marginBottom: 16 },
                    w: theme.logo.w * 0.6,
                    h: theme.logo.h * 0.6,
                    texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w * 0.6, theme.logo.h * 0.6)
                },
                Title: {
                    flexItem: { marginBottom: 10 },
                    type: TranslatableText,
                    key: "pin.title",
                    text: {
                        textAlign: "center",
                        fontFace: "SemiBold",
                        fontSize: 28,
                        textColor: Colors(theme.colors.text.primary).get()
                    }
                },
                Subtitle: {
                    flexItem: { marginBottom: 16 },
                    type: TranslatableText,
                    key: "",
                    text: {
                        textAlign: "center",
                        fontSize: 18,
                        textColor: Colors(theme.colors.text.secondary).get(),
                        wordWrap: true,
                        wordWrapWidth: CARD_W - 60
                    },
                    alpha: 0
                },
                PinBoxes: {
                    flexItem: { marginBottom: 12 },
                    w: pinRowWidth,
                    h: BOX_SIZE,
                    flex: { direction: "row", alignItems: "center" },
                    ...Object.fromEntries(
                        Array.from({ length: 4 }, (_, i) => [
                            `Box${i + 1}`,
                            {
                                w: BOX_SIZE, h: BOX_SIZE,
                                flexItem: i < 3 ? { marginRight: BOX_SPACING } : {},
                                rect: true, color: 0xffffffff,
                                shader: { type: Lightning.shaders.RoundedRectangle, radius: 8, stroke: 0, strokeColor: 0xffffffff },
                                Label: {
                                    x: BOX_SIZE / 2, y: BOX_SIZE / 2, mountX: 0.5, mountY: 0.5,
                                    text: { text: "0", fontSize: 24, fontFace: "SemiBold", textColor: 0xff1565c0, textAlign: "center" }
                                }
                            }
                        ])
                    )
                },
                Error: {
                    type: TranslatableText,
                    key: "pin.enter",
                    h: 24,
                    text: { textAlign: "center", fontSize: 16, textColor: Colors("#ff4444").get() },
                    alpha: 0
                },
                SubmitWrapper: {
                    flexItem: { marginTop: 12 },
                    w: 300, h: (theme.button.h || 56) + 2,
                    Border: {
                        x: -1, y: -1, w: 302, h: (theme.button.h || 56) + 2,
                        rect: true, color: 0x00000000,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: theme.button.radius || 8, stroke: 2, strokeColor: 0xffffffff }
                    },
                    SubmitBtn: {
                        type: Button, w: 300,
                        label: "pin.play",
                        actionId: "pinSubmit"
                    }
                }
            }
        };
    }

    readonly Card = this.getByRef("Card")! as any;

    override _init() {
        this._setState("PinBoxesState");
    }

    override _active() {
        if (pendingCallbacks) {
            const cb = pendingCallbacks;
            clearPinInputCallbacks();
            this.open(
                cb.onSuccess,
                cb.onCancel,
                "verify",
                undefined,
                4,
                cb.submitLabel,
                cb.subtitleKey
            );
        }
    }

    static override _states() {
        return [
            class PinBoxesState extends this {
                override _getFocused(): any {
                    return this;
                }

                override _handleDown() {
                    if (this._isVerifying) return false;
                    if (this._pin.length === this._digitCount) {
                        this._focusArea = "submit";
                        this._setState("SubmitState");
                    }
                    return true;
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return false;
                    const key = event.key;
                    const numKey = +key;

                    if (!isNaN(numKey) && numKey >= 0 && numKey <= 9 && this._pin.length < this._digitCount) {
                        this._pin.push(key);
                        this._focusedBoxIndex = this._pin.length;
                        this._updatePinBoxes();
                        this._hideError();

                        if (this._pin.length === this._digitCount) {
                            this._focusArea = "submit";
                            this._setState("SubmitState");
                        }
                    }

                    if (key === "Backspace" || key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._hideError();
                        }
                    }
                }
            },
            class SubmitState extends this {
                override _getFocused(): any {
                    const wrapper = this.Card.getByRef("SubmitWrapper");
                    return wrapper?.getByRef("SubmitBtn") || this;
                }

                override _handleUp() {
                    if (this._isVerifying) return;
                    this._focusArea = "boxes";
                    this._setState("PinBoxesState");
                }

                override _handleKey(event: KeyboardEvent) {
                    if (this._isVerifying) return;
                    if (event.key === "Backspace" || event.key === "Delete") {
                        if (this._pin.length > 0) {
                            this._pin.pop();
                            this._focusedBoxIndex = this._pin.length;
                            this._updatePinBoxes();
                            this._hideError();
                            this._focusArea = "boxes";
                            this._setState("PinBoxesState");
                        }
                    }
                }
            }
        ];
    }

    override _captureKey(e: KeyboardEvent) {
        if ((e.key === "Enter" || e.keyCode === 13) && this._focusArea === "submit") {
            if (this._pin.length === this._digitCount) {
                this._verifyPin();
            }
            return true;
        }
        return false;
    }

    $handleBtnClick(id: string) {
        if (id === "pinSubmit" && this._pin.length === this._digitCount) {
            this._verifyPin();
        }
    }

    override _handleBack() {
        this._onCancel?.();
        this.close();
        return true;
    }

    // === PIN BOX UPDATES ===

    _updatePinBoxes() {
        const boxes = this.Card.getByRef("PinBoxes") as any;
        if (!boxes) return;

        for (let i = 0; i < 4; i++) {
            const box = boxes.getByRef(`Box${i + 1}`) as any;
            if (!box) continue;
            const shouldShow = i < this._digitCount;
            const hasValue = i < this._pin.length;
            const label = box.getByRef("Label") as any;

            if (hasValue) {
                if (label) label.patch({ text: { text: "●", fontSize: 28 } });
            } else if (i === this._pin.length) {
                if (label) label.patch({ text: { text: "_", fontSize: 24 } });
            } else {
                if (label) label.patch({ text: { text: "0", fontSize: 24 } });
            }

            box.patch({ alpha: shouldShow ? 1 : 0, visible: shouldShow });
        }
    }

    _hideError() {
        const error = this.Card.getByRef("Error") as any;
        if (error) error.patch({ alpha: 0 });
    }

    _showError() {
        const error = this.Card.getByRef("Error") as any;
        if (error) error.patch({ alpha: 1 });
    }

    // === PIN VERIFICATION ===

    async _verifyPin() {
        if (this._pin.length !== this._digitCount) {
            this._showError();
            return;
        }

        this._isVerifying = true;
        const pin = this._pin.join("");

        // Mode: set
        if (this._mode === "set") {
            this._isVerifying = false;
            this._firstPin = pin;
            this._pin = [];
            this._focusedBoxIndex = 0;
            this._mode = "confirm";
            const title = this.Card.getByRef("Title") as any;
            if (title) title.patch({ key: "pin.confirmTitle" });
            this._updatePinBoxes();
            this._hideError();
            this._focusArea = "boxes";
            this._setState("PinBoxesState");
            return;
        }

        // Mode: confirm
        if (this._mode === "confirm") {
            this._isVerifying = false;
            if (pin === this._firstPin) {
                this._onPinSet?.(pin);
                this._onSuccess?.();
                this.close();
            } else {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._firstPin = undefined;
                this._mode = "set";
                this._updatePinBoxes();
                const error = this.Card.getByRef("Error") as any;
                if (error) error.patch({ key: "pin.mismatch", alpha: 1 });
                const title = this.Card.getByRef("Title") as any;
                if (title) title.patch({ key: "pin.setTitle" });
                this._focusArea = "boxes";
                this._setState("PinBoxesState");
            }
            return;
        }

        // Mode: age
        if (this._mode === "age") {
            this._isVerifying = false;
            const age = parseInt(pin, 10);
            if (isNaN(age) || age < 0 || age > 99) {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._updatePinBoxes();
                const error = this.Card.getByRef("Error") as any;
                if (error) error.patch({ key: "pin.invalidAge", alpha: 1 });
                this._focusArea = "boxes";
                this._setState("PinBoxesState");
                return;
            }
            const ageString = age < 10 ? `0${age}` : `${age}`;
            this._onPinSet?.(ageString);
            this._onSuccess?.();
            this.close();
            return;
        }

        // Mode: verify
        try {
            let isValid = false;
            const user = Storage.get(STORAGE_KEYS.user) as any;
            const storedPin = user?.settings?.parentalPin || Storage.get(STORAGE_KEYS.parentalPin);

            if (storedPin) {
                isValid = String(storedPin) === String(pin);
            }

            if (!isValid && !storedPin) {
                if (isPostTvLinux()) {
                    isValid = await new Promise<boolean>((resolve) => {
                        try {
                            const timeoutId = setTimeout(() => resolve(false), 5000);
                            postTvWebSocket.getSettings("pics.pin", (response) => {
                                clearTimeout(timeoutId);
                                try {
                                    const settingsPin = response?.data?.value || response?.value || "";
                                    resolve(String(settingsPin) === String(pin));
                                } catch (e) { resolve(false); }
                            });
                        } catch (e) { resolve(false); }
                    });
                } else if (isPostTvAndroid()) {
                    try {
                        const android = window.Android;
                        if (android && typeof android.testPinCode === "function") {
                            const result = android.testPinCode(pin);
                            isValid = typeof result === "boolean" ? result : await Promise.resolve(result);
                        }
                    } catch (e) { isValid = false; }
                }
            }

            this._isVerifying = false;

            if (isValid) {
                const onSuccess = this._onSuccess;
                this.close();
                onSuccess?.();
            } else {
                this._pin = [];
                this._focusedBoxIndex = 0;
                this._updatePinBoxes();
                this._showError();
                this._focusArea = "boxes";
                this._setState("PinBoxesState");
            }
        } catch (e) {
            this._isVerifying = false;
            this._showError();
        }
    }

    // === OPEN / CLOSE ===

    override _focus() {
        this.patch({ smooth: { alpha: 1 } });
    }

    override _unfocus() {
        this.patch({ smooth: { alpha: 0 } });
    }

    open(
        onSuccess?: () => void,
        onCancel?: () => void,
        mode: "verify" | "set" | "confirm" | "age" = "verify",
        onPinSet?: (pin: string) => void,
        digitCount: number = 4,
        submitLabel?: string,
        subtitleKey?: string
    ) {
        this._onSuccess = onSuccess;
        this._onCancel = onCancel;
        this._mode = mode;
        this._onPinSet = onPinSet;
        this._digitCount = digitCount;
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._firstPin = undefined;
        this._focusArea = "boxes";

        // Update title
        const title = this.Card.getByRef("Title") as any;
        if (mode === "set") {
            if (title) title.patch({ key: "pin.setTitle" });
        } else if (mode === "confirm") {
            if (title) title.patch({ key: "pin.confirmTitle" });
        } else if (mode === "age") {
            if (title) title.patch({ key: "pin.ageTitle" });
        } else {
            if (title) title.patch({ key: "pin.title" });
        }

        // Update subtitle
        const subtitle = this.Card.getByRef("Subtitle") as any;
        if (subtitleKey) {
            if (subtitle) subtitle.patch({ key: subtitleKey, alpha: 1 });
        } else {
            if (subtitle) subtitle.patch({ alpha: 0 });
        }

        // Update submit button label
        const wrapper = this.Card.getByRef("SubmitWrapper") as any;
        const btn = wrapper?.getByRef("SubmitBtn");
        if (btn) {
            btn.label = submitLabel || "pin.submit";
        }

        this._updatePinBoxes();
        this._hideError();
        this._setState("PinBoxesState");

        this.patch({ alpha: 1, visible: true });
        Router.focusWidget("PinInput");

        setTimeout(() => {
            this._focus();
            this._refocus();
        }, 150);
    }

    close() {
        this._pin = [];
        this._focusedBoxIndex = 0;
        this._isVerifying = false;
        this._mode = "verify";
        this._onSuccess = undefined;
        this._onCancel = undefined;
        this._onPinSet = undefined;
        this._firstPin = undefined;
        this._focusArea = "boxes";
        this._setState("PinBoxesState");
        Router.focusPage();
    }

    getPin(): string {
        return this._pin.join("");
    }
}
